/**
 * Our own annotations, stripped before a line's text reaches a supplier — or the comparison, or
 * the customer quotation (Akhil, 2026-10-02: "don't ever append"; the RFQ builder shows the
 * customer's unit as its own label).
 *
 * Since 2026-10-02 they are written only to `raw_text`, the hidden record the RFQ builder's labels
 * read; never to the printed description. Lines and quotations made before then still carry them,
 * which is why every printed path strips them rather than trusting the stored text.
 *
 * Two places append a note to the customer's text, both for the purchaser's benefit and neither
 * meant to leave the building:
 *
 *   `useMailExtraction.ts`'s `verifyItemCodes` → "(code 3901010001TEST not in catalogue)"
 *   `features/mail/createEnquiryLines.ts`      → "(customer's unit: DRUMS)"
 *
 * Both land on `Opportunity Item.description`, which is what `raw_text` reads back and what the
 * RFQ workbook prints. A supplier reading "code … not in catalogue" is reading a note we wrote to
 * ourselves about a code WE could not resolve — it says nothing about the item they are pricing,
 * and it was observed on a real sheet (fix list point 11).
 *
 * Anchored to the end of the string and applied repeatedly, because a line can carry both.
 */
const INTERNAL_ANNOTATIONS = [
  /\s*\(code [^)]*? not in catalogue\)\s*$/i,
  /\s*\(customer['’]s unit:[^)]*\)\s*$/i,
]

export function withoutInternalAnnotations(text: string): string {
  let out = text
  for (;;) {
    const before = out
    for (const pattern of INTERNAL_ANNOTATIONS) out = out.replace(pattern, '').trim()
    if (out === before) return out
  }
}


/** What prints for a line: the edited description, else the customer's words — never our notes. */
export function printedDescription(line: { description?: string | null; raw_text?: string | null }): string {
  return withoutInternalAnnotations((line.description ?? '').trim() || (line.raw_text ?? ''))
}
